/**
 * Local-First Database Client
 *
 * ISSUE_038: IndexedDB as Source of Truth
 * Uses Dexie.js for IndexedDB access with sync metadata.
 *
 * @module db/client
 */

import Dexie, { type Table } from "dexie";
import type { SlotSessionType } from "~/types/session";
import type { ILocalDatabase, IVaultTable } from "./interfaces";
import { createDexieAdapter } from "./dexie/DexieAdapter";
import { isTauri } from "./platform";

// ============================================================================
// CACHE ENTRY (ISSUE_051)
// ============================================================================

/**
 * Generic cache entry for Read-Through Cache pattern
 */
export interface CacheEntry<T> {
  /** Cache key (workspaceId or composite key) */
  key: string;
  /** Cached data */
  data: T;
  /** Last updated epoch ms */
  updatedAt: number;
}

// ============================================================================
// PENDING MUTATION (ISSUE_052)
// ============================================================================

/**
 * Pending mutation for local-first write path.
 * Queued writes are stored here until successfully pushed to Supabase.
 */
export interface PendingMutation {
  /** Auto-increment primary key */
  id?: number;
  /** Entity type: 'sessionType' | 'student' | 'teacher' | 'permission' | 'settings' | 'theme' */
  entityType: string;
  /** Target record ID (or composite key) */
  entityId: string;
  /** Operation type */
  operation: "create" | "update" | "delete";
  /** Data payload to push to Supabase */
  payload: unknown;
  /** Cache key for invalidation */
  cacheKey: string;
  /** Created epoch ms */
  createdAt: number;
  /** Retry count */
  retryCount: number;
  /** Last error message */
  lastError?: string;
  /**
   * Mutation status.
   * - "pending": 대기 중 (push 시도 대상)
   * - "failed": MAX_RETRIES 초과로 push 실패 — 로컬 데이터 보호 유지, 사용자 재시도 대기
   * undefined는 "pending"으로 취급 (하위 호환)
   */
  status?: "pending" | "failed";
}

// ============================================================================
// SYNC METADATA
// ============================================================================

/**
 * Sync status for local entities
 */
export type SyncStatus = "synced" | "pending" | "conflict" | "error";

/**
 * Pending operation type
 */
export type PendingOp = "create" | "update" | "delete";

/**
 * Sync metadata embedded in each entity
 */
export interface SyncMeta {
  /** Local modification timestamp (epoch ms) */
  localUpdatedAt: number;
  /** Remote modification timestamp from server (epoch ms) */
  remoteUpdatedAt: number;
  /** Current sync status */
  syncStatus: SyncStatus;
  /** Pending operation to sync (undefined when synced) */
  pendingOp: PendingOp | undefined;
  /** Error message if sync failed (only when syncStatus === "error") */
  syncError?: string;
  /**
   * FK 위반 push 재시도 횟수 (ISSUE_169 #4). FK 위반은 참조 row(예: 학생)가 늦게 동기화되면
   * 풀리는 시간 의존 에러라 즉시 permanent 격리하지 않고 제한 재시도 — 성공 시 리셋.
   */
  pushAttempts?: number | undefined;
}

// ============================================================================
// LOCAL SESSION
// ============================================================================

/**
 * Local Session stored in IndexedDB
 *
 * Combines session data with sync metadata.
 * This is the Source of Truth for the app.
 */
export interface LocalSession {
  // === Identity ===
  id: string;

  // === Core Data ===
  studentId: string;
  studentName: string;
  teacherId: string;
  teacherName: string;
  teacherColor: string;
  sessionType: SlotSessionType;
  date: string; // YYYY-MM-DD

  // === Time ===
  /** @deprecated use startMs */
  startHour: number;
  /** @deprecated use endMs */
  endHour: number;
  /** epoch ms — source of truth (ISSUE_054) */
  startMs: number;
  /** epoch ms — source of truth (ISSUE_054) */
  endMs: number;

  // === Optional ===
  memo: string | undefined;
  approvalStatus: "pending" | "approved" | "rejected";
  approvedAt: number | undefined;

  // === Course (Flow Architecture) ===
  courseId?: string;
  /** 주차계획 회차 번호 (nullable, 코스 기반 세션만) */
  syllabusOrder?: number;

  // === Reconcile (ISSUE_080) ===
  /** 코스↔세션 동기화 상태: managed(자동조정 대상) | user_modified(사용자 수정으로 보호) | detached(연결 해제) */
  syncState: "managed" | "user_modified" | "detached";
  /** 세션 생성 출처: auto(코스 기반 자동 생성) | manual(사용자 수동 생성) */
  sourceType: "auto" | "manual";

  // === Audit ===
  createdBy?: string;
  updatedBy?: string;
  createdByName?: string;
  updatedByName?: string;
  createdAt?: string;   // ISO timestamp
  updatedAt?: string;   // ISO timestamp

  // === Workspace ===
  workspaceId: string; // academyId or 'scratch:{userId}'

  // === Sync Metadata ===
  _syncMeta: SyncMeta;
}

// ============================================================================
// SYNC STATE
// ============================================================================

/**
 * Global sync state for tracking overall sync progress
 */
export interface SyncState {
  id: string; // 'global' or workspaceId
  lastSyncAt: number;
  lastPullAt: number;
  lastPushAt: number;
  isPulling: boolean;
  isPushing: boolean;
  pendingCount: number;
  conflictCount: number;
  error: string | undefined;
}

// ============================================================================
// DATABASE CLASS
// ============================================================================

/**
 * Noten Local Database
 *
 * IndexedDB wrapper using Dexie.js
 */
export class NotenDatabase extends Dexie {
  sessions!: Table<LocalSession>;
  syncState!: Table<SyncState>;
  // ISSUE_051: Cache tables for Local-First master data
  // Dexie Table<T> is invariant — `any` is required because cache tables store heterogeneous data.
  // eslint-disable @typescript-eslint/no-explicit-any -- Dexie Table invariance
  cachedSessionTypes!: Table<CacheEntry<any>>;
  cachedStudents!: Table<CacheEntry<any>>;
  cachedTeachers!: Table<CacheEntry<any>>;
  cachedPermissions!: Table<CacheEntry<any>>;
  cachedSettings!: Table<CacheEntry<any>>;
  cachedThemes!: Table<CacheEntry<any>>;
  cachedHolidays!: Table<CacheEntry<any>>;
  cachedProfiles!: Table<CacheEntry<any>>;
  cachedFlows!: Table<CacheEntry<any>>;
  cachedCourses!: Table<CacheEntry<any>>;
  cachedClassrooms!: Table<CacheEntry<any>>;
  cachedBranches!: Table<CacheEntry<any>>;
  cachedAcademyInfo!: Table<CacheEntry<any>>;
  cachedScheduleTemplates!: Table<CacheEntry<any>>;
  cachedExams!: Table<CacheEntry<any>>; // v39 이하 호환, v40에서 삭제
  cachedScores!: Table<CacheEntry<any>>; // ISSUE_106
  cachedStudentStats!: Table<CacheEntry<any>>;
  cachedSchools!: Table<CacheEntry<any>>;
  cachedTextbooks!: Table<CacheEntry<any>>;
  cachedTodos!: Table<CacheEntry<any>>;
  cachedNotebooks!: Table<CacheEntry<any>>;
  cachedQuestions!: Table<CacheEntry<any>>; // ISSUE_112
  cachedQuestionSets!: Table<CacheEntry<any>>; // ISSUE_116
  cachedQuestionLinks!: Table<CacheEntry<any>>; // CORE_001
  cachedExamPapers!: Table<CacheEntry<any>>;
  cachedChannels!: Table<CacheEntry<any>>; // ISSUE_149 Phase 2
  cachedChannelMessages!: Table<CacheEntry<any>>; // ISSUE_149 Phase 2
  // 스키마(stores)엔 있었으나 클래스 타입 선언이 누락돼 있던 것들 — (db as any) 캐스팅이 이 드리프트를
  // 숨겨왔다. 선언을 스키마와 일치시켜 타입이 정직하게 되도록 복구(2026-07-16, as any 제거 중 발견).
  cachedConceptNodes!: Table<CacheEntry<any>>; // CORE_001
  cachedClassGroups!: Table<CacheEntry<any>>;
  cachedRegularLessons!: Table<CacheEntry<any>>;
  cachedTracks!: Table<CacheEntry<any>>;
  // eslint-enable @typescript-eslint/no-explicit-any
  // ISSUE_052: Pending mutations queue for local-first writes
  pendingMutations!: Table<PendingMutation, number>;

  constructor() {
    super("noten");

    // Schema version 1
    this.version(1).stores({
      // Sessions: indexed by id, date, teacherId, workspaceId, syncStatus
      sessions: "id, date, teacherId, workspaceId, [workspaceId+date], _syncMeta.syncStatus",
      // Sync state: indexed by id
      syncState: "id",
    });

    // Schema version 2: Add studentId index for conflict checking
    this.version(2).stores({
      sessions: "id, date, teacherId, studentId, workspaceId, [workspaceId+date], _syncMeta.syncStatus",
      syncState: "id",
    });

    // Schema version 21: Ensure studentId index exists (for DBs that were at version 20)
    this.version(21).stores({
      sessions: "id, date, teacherId, studentId, workspaceId, [workspaceId+date], _syncMeta.syncStatus",
      syncState: "id",
    });

    // ISSUE_051: Schema version 22 — Add cache tables for Local-First master data
    this.version(22).stores({
      sessions: "id, date, teacherId, studentId, workspaceId, [workspaceId+date], _syncMeta.syncStatus",
      syncState: "id",
      cachedSessionTypes: "key",
      cachedStudents: "key",
      cachedTeachers: "key",
      cachedPermissions: "key",
      cachedSettings: "key",
      cachedThemes: "key",
      cachedHolidays: "key",
    });

    // ISSUE_052: Schema version 23 — Add pendingMutations queue for local-first writes
    this.version(23).stores({
      sessions: "id, date, teacherId, studentId, workspaceId, [workspaceId+date], _syncMeta.syncStatus",
      syncState: "id",
      cachedSessionTypes: "key",
      cachedStudents: "key",
      cachedTeachers: "key",
      cachedPermissions: "key",
      cachedSettings: "key",
      cachedThemes: "key",
      cachedHolidays: "key",
      pendingMutations: "++id, entityType, [entityType+entityId]",
    });

    // Schema version 24 — Add cachedProfiles for offline profile access
    this.version(24).stores({
      sessions: "id, date, teacherId, studentId, workspaceId, [workspaceId+date], _syncMeta.syncStatus",
      syncState: "id",
      cachedSessionTypes: "key",
      cachedStudents: "key",
      cachedTeachers: "key",
      cachedPermissions: "key",
      cachedSettings: "key",
      cachedThemes: "key",
      cachedHolidays: "key",
      cachedProfiles: "key",
      pendingMutations: "++id, entityType, [entityType+entityId]",
    });

    // Schema version 25 — Add cachedClassGroups, cachedRegularLessons (legacy)
    this.version(25).stores({
      sessions: "id, date, teacherId, studentId, workspaceId, [workspaceId+date], _syncMeta.syncStatus",
      syncState: "id",
      cachedSessionTypes: "key",
      cachedStudents: "key",
      cachedTeachers: "key",
      cachedPermissions: "key",
      cachedSettings: "key",
      cachedThemes: "key",
      cachedHolidays: "key",
      cachedProfiles: "key",
      cachedClassGroups: "key",
      cachedRegularLessons: "key",
      pendingMutations: "++id, entityType, [entityType+entityId]",
    });

    // Schema version 26 — Flow Architecture: rename cache tables
    this.version(26).stores({
      sessions: "id, date, teacherId, studentId, workspaceId, [workspaceId+date], _syncMeta.syncStatus",
      syncState: "id",
      cachedSessionTypes: "key",
      cachedStudents: "key",
      cachedTeachers: "key",
      cachedPermissions: "key",
      cachedSettings: "key",
      cachedThemes: "key",
      cachedHolidays: "key",
      cachedProfiles: "key",
      cachedClassGroups: null, // delete old table
      cachedRegularLessons: null, // delete old table
      cachedFlows: "key",
      cachedCourses: "key",
      pendingMutations: "++id, entityType, [entityType+entityId]",
    });

    // Schema version 27 — Add cachedClassrooms for classroom management
    this.version(27).stores({
      sessions: "id, date, teacherId, studentId, workspaceId, [workspaceId+date], _syncMeta.syncStatus",
      syncState: "id",
      cachedSessionTypes: "key",
      cachedStudents: "key",
      cachedTeachers: "key",
      cachedPermissions: "key",
      cachedSettings: "key",
      cachedThemes: "key",
      cachedHolidays: "key",
      cachedProfiles: "key",
      cachedFlows: "key",
      cachedCourses: "key",
      cachedClassrooms: "key",
      pendingMutations: "++id, entityType, [entityType+entityId]",
    });

    // Schema version 28 — Add cachedBranches for branch management
    this.version(28).stores({
      sessions: "id, date, teacherId, studentId, workspaceId, [workspaceId+date], _syncMeta.syncStatus",
      syncState: "id",
      cachedSessionTypes: "key",
      cachedStudents: "key",
      cachedTeachers: "key",
      cachedPermissions: "key",
      cachedSettings: "key",
      cachedThemes: "key",
      cachedHolidays: "key",
      cachedProfiles: "key",
      cachedFlows: "key",
      cachedCourses: "key",
      cachedClassrooms: "key",
      cachedBranches: "key",
      pendingMutations: "++id, entityType, [entityType+entityId]",
    });

    // ISSUE_063: Schema version 29 — Add cachedAcademyInfo for offline academy settings
    this.version(29).stores({
      sessions: "id, date, teacherId, studentId, workspaceId, [workspaceId+date], _syncMeta.syncStatus",
      syncState: "id",
      cachedSessionTypes: "key",
      cachedStudents: "key",
      cachedTeachers: "key",
      cachedPermissions: "key",
      cachedSettings: "key",
      cachedThemes: "key",
      cachedHolidays: "key",
      cachedProfiles: "key",
      cachedFlows: "key",
      cachedCourses: "key",
      cachedClassrooms: "key",
      cachedBranches: "key",
      cachedAcademyInfo: "key",
      pendingMutations: "++id, entityType, [entityType+entityId]",
    });

    // Schema version 30 — Add cachedScheduleTemplates for timetable presets
    this.version(30).stores({
      sessions: "id, date, teacherId, studentId, workspaceId, [workspaceId+date], _syncMeta.syncStatus",
      syncState: "id",
      cachedSessionTypes: "key",
      cachedStudents: "key",
      cachedTeachers: "key",
      cachedPermissions: "key",
      cachedSettings: "key",
      cachedThemes: "key",
      cachedHolidays: "key",
      cachedProfiles: "key",
      cachedFlows: "key",
      cachedCourses: "key",
      cachedClassrooms: "key",
      cachedBranches: "key",
      cachedAcademyInfo: "key",
      cachedScheduleTemplates: "key",
      pendingMutations: "++id, entityType, [entityType+entityId]",
    });

    // Schema version 31 — Add cachedExams + cachedStudentStats for offline statistics
    this.version(31).stores({
      sessions: "id, date, teacherId, studentId, workspaceId, [workspaceId+date], _syncMeta.syncStatus",
      syncState: "id",
      cachedSessionTypes: "key",
      cachedStudents: "key",
      cachedTeachers: "key",
      cachedPermissions: "key",
      cachedSettings: "key",
      cachedThemes: "key",
      cachedHolidays: "key",
      cachedProfiles: "key",
      cachedFlows: "key",
      cachedCourses: "key",
      cachedClassrooms: "key",
      cachedBranches: "key",
      cachedAcademyInfo: "key",
      cachedScheduleTemplates: "key",
      cachedExams: "key",
      cachedStudentStats: "key",
      pendingMutations: "++id, entityType, [entityType+entityId]",
    });

    // Schema version 32 — Add courseId index to sessions for course→session queries
    this.version(32).stores({
      sessions: "id, date, teacherId, studentId, courseId, workspaceId, [workspaceId+date], _syncMeta.syncStatus",
      syncState: "id",
      cachedSessionTypes: "key",
      cachedStudents: "key",
      cachedTeachers: "key",
      cachedPermissions: "key",
      cachedSettings: "key",
      cachedThemes: "key",
      cachedHolidays: "key",
      cachedProfiles: "key",
      cachedFlows: "key",
      cachedCourses: "key",
      cachedClassrooms: "key",
      cachedBranches: "key",
      cachedAcademyInfo: "key",
      cachedScheduleTemplates: "key",
      cachedExams: "key",
      cachedStudentStats: "key",
      pendingMutations: "++id, entityType, [entityType+entityId]",
    });

    // Schema version 33 — ISSUE_079: pendingMutations에 status 인덱스 추가
    // "failed" mutation도 로컬 데이터 보호를 위해 보존 (silent deletion 방지)
    this.version(33).stores({
      sessions: "id, date, teacherId, studentId, courseId, workspaceId, [workspaceId+date], _syncMeta.syncStatus",
      syncState: "id",
      cachedSessionTypes: "key",
      cachedStudents: "key",
      cachedTeachers: "key",
      cachedPermissions: "key",
      cachedSettings: "key",
      cachedThemes: "key",
      cachedHolidays: "key",
      cachedProfiles: "key",
      cachedFlows: "key",
      cachedCourses: "key",
      cachedClassrooms: "key",
      cachedBranches: "key",
      cachedAcademyInfo: "key",
      cachedScheduleTemplates: "key",
      cachedExams: "key",
      cachedStudentStats: "key",
      pendingMutations: "++id, entityType, status, [entityType+entityId]",
    });

    // Schema version 34 — pendingMutations에 cacheKey 인덱스 추가
    // mergeServerWithLocal이 인덱스 쿼리로 해당 캐시 키의 pending mutations 조회
    this.version(34).stores({
      sessions: "id, date, teacherId, studentId, courseId, workspaceId, [workspaceId+date], _syncMeta.syncStatus",
      syncState: "id",
      cachedSessionTypes: "key",
      cachedStudents: "key",
      cachedTeachers: "key",
      cachedPermissions: "key",
      cachedSettings: "key",
      cachedThemes: "key",
      cachedHolidays: "key",
      cachedProfiles: "key",
      cachedFlows: "key",
      cachedCourses: "key",
      cachedClassrooms: "key",
      cachedBranches: "key",
      cachedAcademyInfo: "key",
      cachedScheduleTemplates: "key",
      cachedExams: "key",
      cachedStudentStats: "key",
      pendingMutations: "++id, entityType, status, cacheKey, [entityType+entityId]",
    });

    // ISSUE_080: Schema version 35 — sessions에 syncState 인덱스 추가 (Reconcile Engine)
    this.version(35).stores({
      sessions: "id, date, teacherId, studentId, courseId, syncState, workspaceId, [workspaceId+date], _syncMeta.syncStatus",
      syncState: "id",
      cachedSessionTypes: "key",
      cachedStudents: "key",
      cachedTeachers: "key",
      cachedPermissions: "key",
      cachedSettings: "key",
      cachedThemes: "key",
      cachedHolidays: "key",
      cachedProfiles: "key",
      cachedFlows: "key",
      cachedCourses: "key",
      cachedClassrooms: "key",
      cachedBranches: "key",
      cachedAcademyInfo: "key",
      cachedScheduleTemplates: "key",
      cachedExams: "key",
      cachedStudentStats: "key",
      pendingMutations: "++id, entityType, status, cacheKey, [entityType+entityId]",
    }).upgrade((tx) => {
      // 기존 세션에 기본값 설정
      return tx.table("sessions").toCollection().modify((session: any) => {
        if (session.syncState === undefined) session.syncState = "managed";
        if (session.sourceType === undefined) session.sourceType = "auto";
      });
    });

    // ISSUE_081: Schema version 36 — Add cachedSchools for school management
    this.version(36).stores({
      sessions: "id, date, teacherId, studentId, courseId, syncState, workspaceId, [workspaceId+date], _syncMeta.syncStatus",
      syncState: "id",
      cachedSessionTypes: "key",
      cachedStudents: "key",
      cachedTeachers: "key",
      cachedPermissions: "key",
      cachedSettings: "key",
      cachedThemes: "key",
      cachedHolidays: "key",
      cachedProfiles: "key",
      cachedFlows: "key",
      cachedCourses: "key",
      cachedClassrooms: "key",
      cachedBranches: "key",
      cachedAcademyInfo: "key",
      cachedScheduleTemplates: "key",
      cachedExams: "key",
      cachedStudentStats: "key",
      cachedSchools: "key",
      pendingMutations: "++id, entityType, status, cacheKey, [entityType+entityId]",
    });

    // Schema version 37 — Add cachedTextbooks for textbook management
    this.version(37).stores({
      sessions: "id, date, teacherId, studentId, courseId, syncState, workspaceId, [workspaceId+date], _syncMeta.syncStatus",
      syncState: "id",
      cachedSessionTypes: "key",
      cachedStudents: "key",
      cachedTeachers: "key",
      cachedPermissions: "key",
      cachedSettings: "key",
      cachedThemes: "key",
      cachedHolidays: "key",
      cachedProfiles: "key",
      cachedFlows: "key",
      cachedCourses: "key",
      cachedClassrooms: "key",
      cachedBranches: "key",
      cachedAcademyInfo: "key",
      cachedScheduleTemplates: "key",
      cachedExams: "key",
      cachedStudentStats: "key",
      cachedSchools: "key",
      cachedTextbooks: "key",
      pendingMutations: "++id, entityType, status, cacheKey, [entityType+entityId]",
    });

    // Schema version 38 — Add cachedTodos for daily log management
    this.version(38).stores({
      sessions: "id, date, teacherId, studentId, courseId, syncState, workspaceId, [workspaceId+date], _syncMeta.syncStatus",
      syncState: "id",
      cachedSessionTypes: "key",
      cachedStudents: "key",
      cachedTeachers: "key",
      cachedPermissions: "key",
      cachedSettings: "key",
      cachedThemes: "key",
      cachedHolidays: "key",
      cachedProfiles: "key",
      cachedFlows: "key",
      cachedCourses: "key",
      cachedClassrooms: "key",
      cachedBranches: "key",
      cachedAcademyInfo: "key",
      cachedScheduleTemplates: "key",
      cachedExams: "key",
      cachedStudentStats: "key",
      cachedSchools: "key",
      cachedTextbooks: "key",
      cachedTodos: "key",
      pendingMutations: "++id, entityType, status, cacheKey, [entityType+entityId]",
    });

    // ISSUE_089: Schema version 39 — Add cachedNotebooks for notebook management
    this.version(39).stores({
      sessions: "id, date, teacherId, studentId, courseId, syncState, workspaceId, [workspaceId+date], _syncMeta.syncStatus",
      syncState: "id",
      cachedSessionTypes: "key",
      cachedStudents: "key",
      cachedTeachers: "key",
      cachedPermissions: "key",
      cachedSettings: "key",
      cachedThemes: "key",
      cachedHolidays: "key",
      cachedProfiles: "key",
      cachedFlows: "key",
      cachedCourses: "key",
      cachedClassrooms: "key",
      cachedBranches: "key",
      cachedAcademyInfo: "key",
      cachedScheduleTemplates: "key",
      cachedExams: "key",
      cachedStudentStats: "key",
      cachedSchools: "key",
      cachedTextbooks: "key",
      cachedTodos: "key",
      cachedNotebooks: "key",
      pendingMutations: "++id, entityType, status, cacheKey, [entityType+entityId]",
    });

    // ISSUE_105: Schema version 40 — cachedExams → cachedTracks (트랙 모듈 교체)
    this.version(40).stores({
      sessions: "id, date, teacherId, studentId, courseId, syncState, workspaceId, [workspaceId+date], _syncMeta.syncStatus",
      syncState: "id",
      cachedSessionTypes: "key",
      cachedStudents: "key",
      cachedTeachers: "key",
      cachedPermissions: "key",
      cachedSettings: "key",
      cachedThemes: "key",
      cachedHolidays: "key",
      cachedProfiles: "key",
      cachedFlows: "key",
      cachedCourses: "key",
      cachedClassrooms: "key",
      cachedBranches: "key",
      cachedAcademyInfo: "key",
      cachedScheduleTemplates: "key",
      cachedExams: null, // 삭제
      cachedTracks: "key", // 신규 — tracks/entries/results 통합 캐시
      cachedStudentStats: "key",
      cachedSchools: "key",
      cachedTextbooks: "key",
      cachedTodos: "key",
      cachedNotebooks: "key",
      pendingMutations: "++id, entityType, status, cacheKey, [entityType+entityId]",
    });

    // ISSUE_106: Schema version 41 — Add cachedScores for SCORES module
    this.version(41).stores({
      sessions: "id, date, teacherId, studentId, courseId, syncState, workspaceId, [workspaceId+date], _syncMeta.syncStatus",
      syncState: "id",
      cachedSessionTypes: "key",
      cachedStudents: "key",
      cachedTeachers: "key",
      cachedPermissions: "key",
      cachedSettings: "key",
      cachedThemes: "key",
      cachedHolidays: "key",
      cachedProfiles: "key",
      cachedFlows: "key",
      cachedCourses: "key",
      cachedClassrooms: "key",
      cachedBranches: "key",
      cachedAcademyInfo: "key",
      cachedScheduleTemplates: "key",
      cachedTracks: "key",
      cachedScores: "key",
      cachedStudentStats: "key",
      cachedSchools: "key",
      cachedTextbooks: "key",
      cachedTodos: "key",
      cachedNotebooks: "key",
      pendingMutations: "++id, entityType, status, cacheKey, [entityType+entityId]",
    });

    // Schema version 42 — cachedTracks 삭제 (TRACKS 모듈 폐기)
    this.version(42).stores({
      sessions: "id, date, teacherId, studentId, courseId, syncState, workspaceId, [workspaceId+date], _syncMeta.syncStatus",
      syncState: "id",
      cachedSessionTypes: "key",
      cachedStudents: "key",
      cachedTeachers: "key",
      cachedPermissions: "key",
      cachedSettings: "key",
      cachedThemes: "key",
      cachedHolidays: "key",
      cachedProfiles: "key",
      cachedFlows: "key",
      cachedCourses: "key",
      cachedClassrooms: "key",
      cachedBranches: "key",
      cachedAcademyInfo: "key",
      cachedScheduleTemplates: "key",
      cachedTracks: null, // 삭제
      cachedScores: "key",
      cachedStudentStats: "key",
      cachedSchools: "key",
      cachedTextbooks: "key",
      cachedTodos: "key",
      cachedNotebooks: "key",
      pendingMutations: "++id, entityType, status, cacheKey, [entityType+entityId]",
    });

    // Schema version 43 — cachedQuestions 추가 (ISSUE_112: 문제은행)
    this.version(43).stores({
      sessions: "id, date, teacherId, studentId, courseId, syncState, workspaceId, [workspaceId+date], _syncMeta.syncStatus",
      syncState: "id",
      cachedSessionTypes: "key",
      cachedStudents: "key",
      cachedTeachers: "key",
      cachedPermissions: "key",
      cachedSettings: "key",
      cachedThemes: "key",
      cachedHolidays: "key",
      cachedProfiles: "key",
      cachedFlows: "key",
      cachedCourses: "key",
      cachedClassrooms: "key",
      cachedBranches: "key",
      cachedAcademyInfo: "key",
      cachedScheduleTemplates: "key",
      cachedScores: "key",
      cachedStudentStats: "key",
      cachedSchools: "key",
      cachedTextbooks: "key",
      cachedTodos: "key",
      cachedNotebooks: "key",
      cachedQuestions: "key",
      pendingMutations: "++id, entityType, status, cacheKey, [entityType+entityId]",
    });

    // Schema version 44 — cachedQuestionSets 추가 (ISSUE_116: 문제집)
    this.version(44).stores({
      sessions: "id, date, teacherId, studentId, courseId, syncState, workspaceId, [workspaceId+date], _syncMeta.syncStatus",
      syncState: "id",
      cachedSessionTypes: "key",
      cachedStudents: "key",
      cachedTeachers: "key",
      cachedPermissions: "key",
      cachedSettings: "key",
      cachedThemes: "key",
      cachedHolidays: "key",
      cachedProfiles: "key",
      cachedFlows: "key",
      cachedCourses: "key",
      cachedClassrooms: "key",
      cachedBranches: "key",
      cachedAcademyInfo: "key",
      cachedScheduleTemplates: "key",
      cachedExams: "key",
      cachedScores: "key",
      cachedStudentStats: "key",
      cachedSchools: "key",
      cachedTextbooks: "key",
      cachedTodos: "key",
      cachedNotebooks: "key",
      cachedQuestions: "key",
      cachedQuestionSets: "key",
      pendingMutations: "++id, entityType, status, cacheKey, [entityType+entityId]",
    });

    // CORE_001: Schema version 45 — cachedQuestionLinks 추가 (문제 간 연결 그래프)
    this.version(45).stores({
      sessions: "id, date, teacherId, studentId, courseId, syncState, workspaceId, [workspaceId+date], _syncMeta.syncStatus",
      syncState: "id",
      cachedSessionTypes: "key",
      cachedStudents: "key",
      cachedTeachers: "key",
      cachedPermissions: "key",
      cachedSettings: "key",
      cachedThemes: "key",
      cachedHolidays: "key",
      cachedProfiles: "key",
      cachedFlows: "key",
      cachedCourses: "key",
      cachedClassrooms: "key",
      cachedBranches: "key",
      cachedAcademyInfo: "key",
      cachedScheduleTemplates: "key",
      cachedExams: "key",
      cachedScores: "key",
      cachedStudentStats: "key",
      cachedSchools: "key",
      cachedTextbooks: "key",
      cachedTodos: "key",
      cachedNotebooks: "key",
      cachedQuestions: "key",
      cachedQuestionSets: "key",
      cachedQuestionLinks: "key",
      pendingMutations: "++id, entityType, status, cacheKey, [entityType+entityId]",
    });

    // CORE_001 Phase 1: Schema version 46 — cachedConceptNodes 추가 (개념 노드)
    this.version(46).stores({
      sessions: "id, date, teacherId, studentId, courseId, syncState, workspaceId, [workspaceId+date], _syncMeta.syncStatus",
      syncState: "id",
      cachedSessionTypes: "key",
      cachedStudents: "key",
      cachedTeachers: "key",
      cachedPermissions: "key",
      cachedSettings: "key",
      cachedThemes: "key",
      cachedHolidays: "key",
      cachedProfiles: "key",
      cachedFlows: "key",
      cachedCourses: "key",
      cachedClassrooms: "key",
      cachedBranches: "key",
      cachedAcademyInfo: "key",
      cachedScheduleTemplates: "key",
      cachedExams: "key",
      cachedScores: "key",
      cachedStudentStats: "key",
      cachedSchools: "key",
      cachedTextbooks: "key",
      cachedTodos: "key",
      cachedNotebooks: "key",
      cachedQuestions: "key",
      cachedQuestionSets: "key",
      cachedQuestionLinks: "key",
      cachedConceptNodes: "key",
      pendingMutations: "++id, entityType, status, cacheKey, [entityType+entityId]",
    });

    // Schema version 47: cachedExamPapers 추가 (시험지 저장)
    this.version(47).stores({
      sessions: "id, date, teacherId, studentId, courseId, syncState, workspaceId, [workspaceId+date], _syncMeta.syncStatus",
      syncState: "id",
      cachedSessionTypes: "key",
      cachedStudents: "key",
      cachedTeachers: "key",
      cachedPermissions: "key",
      cachedSettings: "key",
      cachedThemes: "key",
      cachedHolidays: "key",
      cachedProfiles: "key",
      cachedFlows: "key",
      cachedCourses: "key",
      cachedClassrooms: "key",
      cachedBranches: "key",
      cachedAcademyInfo: "key",
      cachedScheduleTemplates: "key",
      cachedExams: "key",
      cachedScores: "key",
      cachedStudentStats: "key",
      cachedSchools: "key",
      cachedTextbooks: "key",
      cachedTodos: "key",
      cachedNotebooks: "key",
      cachedQuestions: "key",
      cachedQuestionSets: "key",
      cachedQuestionLinks: "key",
      cachedConceptNodes: "key",
      cachedExamPapers: "key",
      pendingMutations: "++id, entityType, status, cacheKey, [entityType+entityId]",
    });

    // ISSUE_149 Phase 2: messaging Local-First layer
    // - cachedChannels: 학원·유저별 채널 목록 (key = channels:{academyId}:{userId})
    // - cachedChannelMessages: 채널별 최근 메시지 (key = messages:{channelId})
    this.version(48).stores({
      sessions: "id, date, teacherId, studentId, courseId, syncState, workspaceId, [workspaceId+date], _syncMeta.syncStatus",
      syncState: "id",
      cachedSessionTypes: "key",
      cachedStudents: "key",
      cachedTeachers: "key",
      cachedPermissions: "key",
      cachedSettings: "key",
      cachedThemes: "key",
      cachedHolidays: "key",
      cachedProfiles: "key",
      cachedFlows: "key",
      cachedCourses: "key",
      cachedClassrooms: "key",
      cachedBranches: "key",
      cachedAcademyInfo: "key",
      cachedScheduleTemplates: "key",
      cachedExams: "key",
      cachedScores: "key",
      cachedStudentStats: "key",
      cachedSchools: "key",
      cachedTextbooks: "key",
      cachedTodos: "key",
      cachedNotebooks: "key",
      cachedQuestions: "key",
      cachedQuestionSets: "key",
      cachedQuestionLinks: "key",
      cachedConceptNodes: "key",
      cachedExamPapers: "key",
      cachedChannels: "key",
      cachedChannelMessages: "key",
      pendingMutations: "++id, entityType, status, cacheKey, [entityType+entityId]",
    });
  }
}

// ============================================================================
// SINGLETON INSTANCE (Proxy 패턴)
// ============================================================================

let _impl: ILocalDatabase | null = null;
let _readyPromise: Promise<void> | null = null;

/**
 * Local database instance (Proxy)
 *
 * 앱 부팅 시 initLocalDb()로 실제 구현체(Dexie 또는 Vault)를 주입.
 * 기존 49+ import 사이트는 변경 불필요.
 *
 * @example
 * import { localDb } from '~/db/client';
 *
 * // Read sessions
 * const sessions = await localDb.sessions.where('date').equals('2026-02-17').toArray();
 *
 * // Write session
 * await localDb.sessions.put(session);
 */
export const localDb: ILocalDatabase = new Proxy({} as ILocalDatabase, {
  get(_, prop: string) {
    if (!_impl) throw new Error("localDb not initialized. Call initLocalDb() first.");
    return (_impl as any)[prop];
  },
});

/**
 * DB 초기화 — 앱 부팅 시 한 번 호출.
 *
 * 브라우저: DexieAdapter (IndexedDB)
 * Tauri:    VaultAdapter (JSON 파일)
 */
export function initLocalDb(): Promise<void> {
  if (_impl) return Promise.resolve();
  if (_readyPromise) return _readyPromise;
  _readyPromise = _initLocalDbInternal();
  return _readyPromise;
}

async function _initLocalDbInternal(): Promise<void> {

  if (isTauri()) {
    // Tauri 환경: plugin-store에서 vault 경로 로드 → VaultAdapter
    const { load } = await import("@tauri-apps/plugin-store");
    const store = await load("settings.json", { autoSave: true, defaults: {} });
    const vaultPath = await store.get<string>("vaultPath");

    if (vaultPath) {
      const { createTauriFileIO } = await import("./vault/fileIO");
      const { VaultManager } = await import("./vault/VaultManager");
      const { createVaultAdapter } = await import("./vault/VaultAdapter");

      const fileIO = createTauriFileIO(vaultPath);
      const manager = new VaultManager(fileIO);

      // Vault가 유효하지 않으면 초기화
      if (!(await manager.isValid())) {
        await manager.init();
      }

      _impl = await createVaultAdapter(fileIO);
      return;
    }
    // vaultPath 없으면 (첫 실행) → Vault 선택 UI에서 처리
    // 일단 Dexie fallback
  }

  // 브라우저 환경 또는 Tauri fallback
  const dexieDb = new NotenDatabase();
  _impl = createDexieAdapter(dexieDb);
  void requestPersistentStorage();
}

/**
 * IndexedDB 를 영구(persistent) 스토리지로 승격 요청 (ISSUE_195 D6).
 *
 * 요청하지 않으면 이 오리진의 저장소는 best-effort 버킷에 남아, 디스크 압박이나
 * 브라우저 정리 정책에 따라 **예고 없이 통째로 삭제될 수 있다**. 무료(미결제) 상태의
 * 세션은 서버에 사본이 없는 유일본이라 그 삭제가 곧 영구 소실이다.
 * 실패해도 앱 동작에는 영향이 없다(권한 거부·미지원 브라우저).
 */
async function requestPersistentStorage(): Promise<void> {
  try {
    if (typeof navigator === "undefined" || !navigator.storage?.persist) return;
    if (await navigator.storage.persisted()) return;
    const granted = await navigator.storage.persist();
    if (!granted) {
      console.info("[localDb] 영구 스토리지 요청이 거부됨 — 브라우저가 로컬 데이터를 정리할 수 있다");
    }
  } catch {
    // 권한 API 미지원/차단 — 무시
  }
}

/**
 * initLocalDb() 완료 대기.
 * 이미 초기화된 경우 즉시 resolve.
 */
export function waitForLocalDb(): Promise<void> {
  if (_impl) return Promise.resolve();
  if (_readyPromise) return _readyPromise;
  return Promise.resolve();
}

/**
 * 내부용: 테스트에서 구현체를 교체하기 위한 함수
 */
export function _setLocalDbImpl(impl: ILocalDatabase | null): void {
  _impl = impl;
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Create a new LocalSession with default sync metadata
 */
export function createLocalSession(
  data: Omit<LocalSession, "_syncMeta" | "syncState" | "sourceType"> & { syncState?: LocalSession["syncState"]; sourceType?: LocalSession["sourceType"] },
  isNew = true
): LocalSession {
  const now = Date.now();
  return {
    syncState: "managed",
    sourceType: "auto",
    ...data,
    _syncMeta: {
      localUpdatedAt: now,
      remoteUpdatedAt: isNew ? 0 : now,
      syncStatus: isNew ? "pending" : "synced",
      pendingOp: isNew ? "create" : undefined,
    },
  };
}

/**
 * Mark a session for update
 */
export function markForUpdate(session: LocalSession): LocalSession {
  return {
    ...session,
    _syncMeta: {
      ...session._syncMeta,
      localUpdatedAt: Date.now(),
      syncStatus: "pending",
      pendingOp: session._syncMeta.pendingOp === "create" ? "create" : "update",
    },
  };
}

/**
 * Mark a session for deletion
 */
export function markForDelete(session: LocalSession): LocalSession {
  return {
    ...session,
    _syncMeta: {
      ...session._syncMeta,
      localUpdatedAt: Date.now(),
      syncStatus: "pending",
      pendingOp: "delete",
    },
  };
}

/**
 * Mark a session as synced (after successful push)
 */
export function markAsSynced(
  session: LocalSession,
  remoteUpdatedAt: number
): LocalSession {
  return {
    ...session,
    _syncMeta: {
      ...session._syncMeta,
      remoteUpdatedAt,
      syncStatus: "synced",
      pendingOp: undefined,
    },
  };
}

/**
 * Get pending sessions for a workspace
 */
export async function getPendingSessions(
  workspaceId: string
): Promise<LocalSession[]> {
  return localDb.sessions
    .where("workspaceId")
    .equals(workspaceId)
    .and((s) => s._syncMeta.syncStatus === "pending")
    .toArray();
}

/**
 * Get sessions by date range for a workspace
 */
export async function getSessionsByDateRange(
  workspaceId: string,
  startDate: string,
  endDate: string
): Promise<LocalSession[]> {
  return localDb.sessions
    .where("[workspaceId+date]")
    .between([workspaceId, startDate], [workspaceId, endDate], true, true)
    .toArray();
}

/**
 * Get sessions by epoch ms range for a workspace
 */
export async function getSessionsByMsRange(
  workspaceId: string,
  startMs: number,
  endMs: number
): Promise<LocalSession[]> {
  return localDb.sessions
    .where("workspaceId")
    .equals(workspaceId)
    .and((s) => s.endMs > startMs && s.startMs < endMs)
    .toArray();
}

// ============================================================================
// LOCAL DATA MANAGEMENT
// ============================================================================

/**
 * Clear all local sessions for a workspace
 * Useful for resetting local state when server data has changed
 */
export async function clearLocalSessions(workspaceId: string): Promise<number> {
  const count = await localDb.sessions
    .where("workspaceId")
    .equals(workspaceId)
    .count();

  await localDb.sessions
    .where("workspaceId")
    .equals(workspaceId)
    .delete();

  // Also reset sync state
  await localDb.syncState.clear();

  return count;
}

/** 이름이 `cached` 로 시작하는 테이블 = 서버가 진실원본인 파생 캐시(지워도 재조회로 복구됨). */
function isCacheTableName(name: string): boolean {
  return name.startsWith("cached");
}

function collectTables(predicate: (name: string) => boolean): IVaultTable<any>[] {
  if (!_impl) throw new Error("localDb not initialized");
  const tables: IVaultTable<any>[] = [];
  for (const [name, value] of Object.entries(_impl)) {
    if (!predicate(name)) continue;
    if (value !== null && typeof value === 'object' && 'clear' in value && typeof (value as any).clear === 'function') {
      tables.push(value as IVaultTable<any>);
    }
  }
  return tables;
}

/**
 * 파생 캐시 테이블(`cached*`)만 비운다 — 서버에서 다시 받아오면 복구되는 것들.
 *
 * 🔴 ISSUE_195 D1: 인증 경로(로그아웃·토큰 만료)는 **반드시 이 함수만** 호출할 것.
 * `sessions` 는 무료(구독 미결제/만료/미로드) 상태에서 **서버에 사본이 없는 유일본**이라,
 * 캐시와 함께 지우면 영구 소실된다. 실제로 그렇게 유실된 사건이 ISSUE_195 의 발단.
 */
export async function clearCachedMasterData(): Promise<void> {
  const tables = collectTables(isCacheTableName);
  await Promise.all(tables.map((t) => t.clear()));
}

/**
 * Clear ALL local data (all tables) — **세션 유일본 포함**.
 *
 * 🔴 ISSUE_195 D1: 되돌릴 수 없다. 인증/로그아웃 경로에서 호출 금지
 * (거기선 `clearCachedMasterData()`). 사용자가 명시적으로 "로컬 데이터 초기화"를
 * 선택했을 때만 쓴다. 미동기 세션이 있으면 기본적으로 거부하며,
 * 정말 버릴 때만 `{ force: true }` 로 넘어간다.
 *
 * ISSUE_100 #9: _impl 동적 순회로 테이블 추가 시 누락 방지
 *
 * @returns 삭제 여부. 미동기 세션 때문에 거부하면 `{ cleared: false, unsyncedCount }`
 */
export async function clearAllLocalData(
  opts: { force?: boolean } = {}
): Promise<{ cleared: boolean; unsyncedCount: number }> {
  if (!_impl) throw new Error("localDb not initialized");

  const unsyncedCount = await countUnsyncedSessions();
  if (unsyncedCount > 0 && !opts.force) {
    console.warn(
      `[localDb] clearAllLocalData 거부 — 서버에 사본이 없는 세션 ${unsyncedCount}건. ` +
      `버리려면 force: true (ISSUE_195 D1)`
    );
    return { cleared: false, unsyncedCount };
  }

  const tables = collectTables(() => true);
  await Promise.all(tables.map((t) => t.clear()));
  return { cleared: true, unsyncedCount };
}

/**
 * 서버에 아직 올라가지 않은 세션 수 — 지우면 복구 불가능한 행의 개수.
 * 무료 상태의 로컬 쓰기는 전부 pendingOp 가 찍히므로(ISSUE_195 D2) 여기 잡힌다.
 */
export async function countUnsyncedSessions(): Promise<number> {
  if (!_impl) return 0;
  // `_syncMeta.syncStatus` 는 인덱스가 있다(v48 스키마). pendingOp 가 찍히는 모든 경로가
  // syncStatus 도 "pending" 으로 함께 올리므로 이 하나로 충분하다.
  return localDb.sessions.where("_syncMeta.syncStatus").equals("pending").count();
}

/**
 * Get local database statistics
 */
export async function getLocalDbStats(): Promise<{
  sessionCount: number;
  pendingCount: number;
  errorCount: number;
  syncedCount: number;
}> {
  const sessions = await localDb.sessions.toArray();
  return {
    sessionCount: sessions.length,
    pendingCount: sessions.filter(s => s._syncMeta.syncStatus === "pending").length,
    errorCount: sessions.filter(s => s._syncMeta.syncStatus === "error").length,
    syncedCount: sessions.filter(s => s._syncMeta.syncStatus === "synced").length,
  };
}
